import { convertMinToDays } from 'utils/time'
import { getAllCycles, getAllVisits } from '../../../../VisitSchedules/routes/VisitSchedulesPage/utils/visitsScheduleUtils'
import { getResolvedCycleStudyDayBounds } from '../projection/engine/cycleResolvedStudyDays'
import { CYCLE_LABELS, MODULE_FALLBACKS } from './constants'
import { projectInstrumentSchedules } from '../projection'
import { clampStudyDay } from '../projection/constants'

export {
  formatTimeOfDay,
  formatRelativeExpirySuffix,
  getExpiryDurationMinutes,
  getInstrumentTimeWindowString,
} from './studyTimelineTimeWindow'
export {
  getChainDeployVisitIds,
  getVisitChainedStudyDay,
  getInstrumentChainedStudyDay,
  getRecurringOccurrenceDays,
  projectInstrumentSchedules,
} from '../projection'

/**
 * Derive scheduled timeline items and cycle ranges from arms, instruments, and study config.
 * Client-side projection only — does not execute schedules.
 */
export const deriveScheduledItemsFromArms = (
  arms,
  selectedArmId,
  instruments = [],
  useAbsoluteStartDate = false,
  absoluteStartDate = null,
  includeVisitCycle = true,
  studyDurationDays = null,
  projectionMode = 'template',
  participantVisits = null,
) => {
  const items = []
  const cycleRanges = []

  const processInstruments = (visits = [], studyEndDay = null, selectedArmVisitIds = null, armsForEngine = []) => {
    const { items: instrumentItems } = projectInstrumentSchedules({
      instruments,
      visits,
      armsForEngine,
      selectedArmId,
      selectedArmVisitIds,
      useAbsoluteStartDate,
      absoluteStartDate,
      studyEndDay,
      projectionMode,
      participantVisits,
    })
    items.push(...instrumentItems)
  }

  const recurringCap =
    studyDurationDays != null && studyDurationDays >= 1 ? Math.floor(Number(studyDurationDays)) : null

  if (!arms || arms.length === 0) {
    processInstruments([], recurringCap, null, arms)
    return {
      items: items.sort((a, b) => a.studyDay - b.studyDay),
      cycleRanges: [],
    }
  }

  if (!selectedArmId) {
    const allVisits = getAllVisits(arms) || []
    processInstruments(allVisits, recurringCap, null, arms)
    return {
      items: items.sort((a, b) => a.studyDay - b.studyDay),
      cycleRanges: [],
    }
  }

  const selectedArmIdStr = selectedArmId != null ? String(selectedArmId) : ''
  const selectedArm =
    arms.find(arm => arm != null && arm.id != null && String(arm.id) === selectedArmIdStr) || arms[0]
  if (!selectedArm) {
    const allVisits = getAllVisits(arms) || []
    processInstruments(allVisits, recurringCap, null, arms)
    return {
      items: items.sort((a, b) => a.studyDay - b.studyDay),
      cycleRanges: [],
    }
  }

  const cycles = (getAllCycles([selectedArm]) || []).filter(
    cycle => !cycle.is_dummy && !cycle.metadata?.is_dummy,
  )
  const visits = getAllVisits([selectedArm]) || []
  const allVisits = getAllVisits(arms) || []

  const visitToCycleMap = new Map()
  cycles.forEach(cycle => {
    if (cycle.visits && Array.isArray(cycle.visits)) {
      cycle.visits.forEach(visit => {
        if (visit.id) {
          visitToCycleMap.set(visit.id, cycle)
        }
      })
    }
  })

  if (includeVisitCycle) {
    const rawRanges = []
    const allArmCycles = cycles
    cycles.forEach(cycle => {
      const bounds = getResolvedCycleStudyDayBounds(cycle, allArmCycles)
      const lengthValueMinutes = cycle.length_value != null ? Number(cycle.length_value) : null

      if (bounds) {
        const startDay = Math.max(1, bounds.start)
        let endDay = bounds.end
        if (lengthValueMinutes != null && lengthValueMinutes >= 0 && Number.isFinite(lengthValueMinutes)) {
          const durationDays = Math.max(1, Math.floor(convertMinToDays(lengthValueMinutes, false)))
          endDay = Math.max(endDay, startDay + durationDays - 1)
        }
        rawRanges.push({
          cycleId: cycle.id,
          cycleName: cycle.name,
          startDay,
          endDay,
          cycleData: cycle,
        })
      }
    })

    rawRanges.sort((a, b) => a.startDay - b.startDay)
    const adjustedRanges = []
    for (let i = 0; i < rawRanges.length; i++) {
      const prev = i > 0 ? adjustedRanges[i - 1] : null
      let startDay = rawRanges[i].startDay
      let endDay = rawRanges[i].endDay
      if (prev != null && startDay <= prev.endDay) {
        const delta = prev.endDay + 1 - startDay
        startDay = prev.endDay + 1
        endDay = endDay + delta
      }
      const range = {
        ...rawRanges[i],
        startDay,
        endDay,
      }
      adjustedRanges.push(range)
      cycleRanges.push(range)
      const cycle = range.cycleData
      items.push({
        id: `cycle-start-${cycle.id}`,
        studyDay: startDay,
        label: `${CYCLE_LABELS.START_PREFIX}${cycle.name}`,
        type: 'cycle',
        cycleName: cycle.name || MODULE_FALLBACKS.CYCLE_NAME_NA,
        window: null,
        cycleData: cycle,
      })
      items.push({
        id: `cycle-end-${cycle.id}`,
        studyDay: endDay,
        label: `${CYCLE_LABELS.END_PREFIX}${cycle.name}`,
        type: 'cycle',
        cycleName: cycle.name || MODULE_FALLBACKS.CYCLE_NAME_NA,
        window: null,
        cycleData: cycle,
      })
    }
  }

  visits.forEach(visit => {
    const raw =
      visit.metadata?.computed_study_day !== undefined
        ? visit.metadata.computed_study_day
        : visit.day !== undefined && visit.day !== null
          ? visit.day
          : null

    if (raw !== null && raw !== undefined) {
      const studyDay = clampStudyDay(raw) ?? raw
      const cycle = includeVisitCycle ? visitToCycleMap.get(visit.id) : null
      items.push({
        id: `visit-${visit.id}`,
        studyDay,
        label: visit.name || visit.metadata?.display_name || MODULE_FALLBACKS.VISIT_LABEL,
        type: 'visit',
        cycleName: includeVisitCycle ? cycle?.name || MODULE_FALLBACKS.CYCLE_NAME_NA : null,
        window: visit.window !== undefined && visit.window !== null ? `±${visit.window}` : null,
        visitId: visit.id,
        visitData: visit,
        cycleId: includeVisitCycle ? cycle?.id : null,
      })
    }
  })

  let studyEndDay = null
  if (studyDurationDays != null && studyDurationDays >= 1) {
    studyEndDay = Math.floor(Number(studyDurationDays))
  }
  if (studyEndDay == null || studyEndDay < 1) {
    studyEndDay = 0
    cycleRanges.forEach(r => {
      if (r.endDay != null && r.endDay >= 1) studyEndDay = Math.max(studyEndDay, r.endDay)
    })
    if (studyEndDay < 1) studyEndDay = null
  }

  const selectedArmVisitIds = new Set(visits.map(v => (v.id != null ? String(v.id) : null)).filter(Boolean))
  processInstruments(allVisits, studyEndDay, selectedArmVisitIds, arms)

  return {
    items: items.sort((a, b) => a.studyDay - b.studyDay),
    cycleRanges: cycleRanges.sort((a, b) => a.startDay - b.startDay),
  }
}
